import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Gift, Smartphone, Sparkles, Store } from 'lucide-react';
import { ApiError, errorMessage } from '../lib/api';
import { useSession } from '../lib/session';
import { useTenant } from '../lib/tenant';
import { useAccountOptions } from '../lib/phase1';
import { Banner, Button, Field, Skeleton } from '../ui/kit';
import { TenantLogo } from '../ui/brand';
import { UpdateBanner } from '../ui/update';

type Stage = { kind: 'form' } | { kind: 'code'; challengeId: string; destination: string; devCode?: string } | { kind: 'exists' };

function Check({ checked, onChange, children, required }: { checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode; required?: boolean }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-[13.5px] leading-snug text-ink-2">
      <input
        type="checkbox"
        className="mt-0.5 size-5 shrink-0 rounded-md accent-[var(--brand)]"
        checked={checked}
        required={required}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{children}</span>
    </label>
  );
}

/**
 * Phase 1 üyelik (QR → bu sayfa). Uygulama indirmek, e-posta ya da SMS gerekmez: ad + telefon + KVKK onayı → üye.
 * Üyelik onayı ile pazarlama izinleri ayrıdır (işaretsiz gelir). İşletme SMS doğrulaması istiyorsa kod adımı eklenir.
 * Sayfa tek istek atar (POST /app/enroll) ve hafif tutulur (ilk açılış kritik yol).
 */
export function Signup() {
  const { bootstrap, loading, path } = useTenant();
  const { enroll } = useSession();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const options = useAccountOptions();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [terms, setTerms] = useState(false);
  const [sms, setSms] = useState(false);
  const [email, setEmail] = useState(false);
  const [website, setWebsite] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'form' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    if (!terms) {
      setError('Devam etmek için üyelik koşullarını onayla.');
      return;
    }
    setBusy(true);
    try {
      const res = await enroll({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        phone: phone.trim(),
        acceptTerms: true,
        termsVersion: bootstrap?.termsVersion,
        smsMarketing: sms,
        emailMarketing: email,
        branchCode: params.get('sube') ?? undefined,
        website,
        ...(stage.kind === 'code' ? { otpChallengeId: stage.challengeId, otpCode: code.trim() } : {}),
      });
      if ('status' in res) {
        setStage({ kind: 'code', challengeId: res.challengeId, destination: res.destination, devCode: res.devCode });
        return;
      }
      nav(path(res.isNewMember ? 'member?hosgeldin=1' : 'member'), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MEMBER_EXISTS') setStage({ kind: 'exists' });
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const smsRecovery = options.data?.recovery.includes('SMS_OTP');

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col">
      <UpdateBanner />
      <section className="card-emerald relative overflow-hidden rounded-b-[36px] px-6 pb-9 pt-safe">
        <div className="pointer-events-none absolute -right-20 -top-10 size-64 rounded-full bg-gold/15 blur-3xl" aria-hidden />
        <div className="pt-9">
          {loading && !bootstrap ? <Skeleton className="size-14 rounded-2xl opacity-40" /> : <TenantLogo size={56} className="animate-pop" />}
          <h1 className="mt-5 animate-rise text-[30px] font-extrabold leading-[1.1] tracking-[-0.03em]">
            {bootstrap?.tenant.name ?? <span className="inline-block h-9 w-48 rounded-lg bg-white/10" />}
          </h1>
          <p className="mt-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-gold-bright/90">Ücretsiz üyelik</p>
          <ul className="mt-5 space-y-2 text-[14px] text-white/80">
            <li className="flex items-center gap-2.5"><Sparkles className="size-4 text-gold-bright" /> Her alışverişte puan kazan</li>
            <li className="flex items-center gap-2.5"><Smartphone className="size-4 text-gold-bright" /> Kartın Apple / Google Wallet'ta — uygulama yok</li>
            <li className="flex items-center gap-2.5"><Gift className="size-4 text-gold-bright" /> Puanlarını ödüle çevir</li>
          </ul>
        </div>
      </section>

      {stage.kind === 'exists' ? (
        <div className="space-y-4 px-5 pt-6">
          <Banner tone="brand" title="Bu telefonla zaten üyesin">
            Kartın telefonunda (Wallet) duruyor olabilir. Yoksa hesabına geri dönebilirsin.
          </Banner>
          {smsRecovery && (
            <Button block size="lg" onClick={() => nav(path(`kurtar?tel=${encodeURIComponent(phone)}`))}>
              SMS koduyla hesabıma dön
            </Button>
          )}
          <div className="flex items-start gap-3 rounded-2xl bg-surface p-4 text-[14px] ring-1 ring-line">
            <Store className="mt-0.5 size-5 shrink-0 text-brand" />
            <p>Kasadaki görevliden <b>hesap bağlantısı</b> iste; ekrandaki karekodu okutunca hesabına dönersin.</p>
          </div>
          <Button block variant="ghost" onClick={() => setStage({ kind: 'form' })}>
            Geri
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 px-5 pt-6" noValidate>
          {stage.kind === 'form' ? (
            <>
              <Field label="Adın" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={60} required autoFocus />
              <Field label="Soyadın (isteğe bağlı)" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={60} />
              <Field label="Telefon" type="tel" inputMode="tel" autoComplete="tel" placeholder="05xx xxx xx xx" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} required hint="Kartını kasada tanımak ve hesabını korumak için. Kartında görünmez." />
              {/* Bot tuzağı: kullanıcı görmez */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden />
              <div className="space-y-3 pt-1">
                <Check checked={terms} onChange={setTerms} required>
                  <Link to={path('kosullar')} className="font-semibold text-ink underline underline-offset-2">Üyelik koşullarını</Link> ve KVKK aydınlatma metnini okudum, kabul ediyorum.
                </Check>
                <Check checked={sms} onChange={setSms}>Kampanyalardan SMS ile haberdar olmak istiyorum (isteğe bağlı).</Check>
                <Check checked={email} onChange={setEmail}>Kampanyalardan e-posta ile haberdar olmak istiyorum (isteğe bağlı).</Check>
              </div>
            </>
          ) : (
            <>
              <Banner tone="brand" title="Telefonunu doğrula">
                {stage.destination} numarasına gelen 6 haneli kodu yaz.
              </Banner>
              <Field label="SMS kodu" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus hint={stage.devCode ? `Geliştirme kodu: ${stage.devCode}` : undefined} />
            </>
          )}
          {error && <p className="text-[14px] font-medium text-danger">{error}</p>}
          <div className="pb-8 pt-2 pb-safe">
            <Button block size="lg" type="submit" loading={busy} disabled={stage.kind === 'form' ? !firstName.trim() || !phone.trim() : code.length !== 6}>
              {stage.kind === 'form' ? 'Üye ol' : 'Doğrula ve üye ol'}
            </Button>
            <p className="mt-4 text-center text-[13px] text-muted">
              Zaten üye misin?{' '}
              <Link to={path('kurtar')} className="font-semibold text-ink-2 underline underline-offset-2">Hesabıma dön</Link>
            </p>
          </div>
        </form>
      )}
    </div>
  );
}

export default Signup;
